/**
 * ================================================================
 * COMPONENT: FirebaseInitializer
 * ================================================================
 *
 * @component        FirebaseInitializer
 * @description      App-level Firebase initialization and service worker registration
 * @category         System Component
 *
 * @props
 * - children: ReactNode - Child components to render
 * - enableFirebase?: boolean - Whether to initialize Firebase
 * - enableServiceWorker?: boolean - Whether to register service worker
 * - onInitialized?: (success: boolean) => void - Callback when initialization completes
 *
 * @responsibilities
 * - Firebase app initialization
 * - Service worker registration for background notifications
 * - Global error handling for Firebase operations
 * - Environment validation and setup verification
 *
 * @placement
 * This component should wrap your app at a high level, typically in main.tsx or App.tsx
 *
 * @usage
 * ```tsx
 * <FirebaseInitializer
 *   enableFirebase={true}
 *   enableServiceWorker={true}
 *   onInitialized={(success) => console.log('Firebase ready:', success)}
 * >
 *   <App />
 * </FirebaseInitializer>
 * ```
 * ================================================================
 */

import React, { useEffect, useState } from 'react';

import { initializeFirebaseApp, isFirebaseConfigured } from '@/lib/firebase/config';

interface FirebaseInitializerProps {
  children: React.ReactNode;
  enableFirebase?: boolean;
  enableServiceWorker?: boolean;
  onInitialized?: (success: boolean, error?: string) => void;
}

export function FirebaseInitializer({
  children,
  enableFirebase = true,
  enableServiceWorker = true,
  onInitialized,
}: FirebaseInitializerProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  useEffect(() => {
    async function initializeFirebase() {
      try {
        console.log('🔥 Starting Firebase initialization...');

        // ================================================================
        // ENVIRONMENT VALIDATION
        // ================================================================
        //
        // Purpose: Check if Firebase is properly configured
        // Safety: Prevent initialization with missing environment variables
        // Feedback: Provide clear guidance for configuration issues
        // ================================================================

        if (!enableFirebase) {
          console.log('🔥 Firebase disabled by configuration');
          setIsInitialized(true);
          onInitialized?.(true);
          return;
        }

        if (!isFirebaseConfigured()) {
          const message =
            'Firebase not configured - missing environment variables. See .env.example for required variables.';
          console.warn('⚠️', message);
          setInitializationError(message);
          setIsInitialized(true);
          onInitialized?.(false, message);
          return;
        }

        // ================================================================
        // FIREBASE APP INITIALIZATION
        // ================================================================
        //
        // Purpose: Initialize Firebase application
        // Error handling: Graceful failure with user feedback
        // Logging: Clear success/failure indicators
        // ================================================================

        const firebaseApp = initializeFirebaseApp();

        if (!firebaseApp) {
          const message = 'Failed to initialize Firebase app';
          console.error('❌', message);
          setInitializationError(message);
          setIsInitialized(true);
          onInitialized?.(false, message);
          return;
        }

        console.log('✅ Firebase app initialized successfully');

        // ================================================================
        // SERVICE WORKER REGISTRATION
        // ================================================================
        //
        // Purpose: Register service worker for background notifications
        // Requirements: HTTPS or localhost for service worker support
        // Fallback: Continue without service worker if registration fails
        // ================================================================

        if (enableServiceWorker && 'serviceWorker' in navigator) {
          try {
            console.log('🔧 Registering Firebase service worker...');

            const registration = await navigator.serviceWorker.register(
              '/firebase-messaging-sw.js',
              {
                scope: '/',
              },
            );

            console.log('✅ Firebase service worker registered successfully:', registration);

            // ================================================================
            // SERVICE WORKER UPDATE HANDLING
            // ================================================================
            //
            // Purpose: Handle service worker updates gracefully
            // UX: Ensure users get latest notification handling
            // Background: Silent updates without disrupting user experience
            // ================================================================

            registration.addEventListener('updatefound', () => {
              console.log('🔄 New service worker version available');

              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    console.log('✅ New service worker installed, will activate on next page load');
                  }
                });
              }
            });
          } catch (swError) {
            console.warn('⚠️ Service worker registration failed:', swError);
            // Continue without service worker - foreground notifications will still work
          }
        } else if (enableServiceWorker) {
          console.warn('⚠️ Service workers not supported in this browser');
        }

        // ================================================================
        // INITIALIZATION COMPLETION
        // ================================================================
        //
        // Purpose: Signal successful initialization
        // State: Update component state for child components
        // Callback: Notify parent components of initialization status
        // ================================================================

        setIsInitialized(true);
        setInitializationError(null);
        onInitialized?.(true);

        console.log('🎉 Firebase initialization completed successfully');
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown initialization error';
        console.error('❌ Firebase initialization failed:', errorMessage);

        setInitializationError(errorMessage);
        setIsInitialized(true);
        onInitialized?.(false, errorMessage);
      }
    }

    initializeFirebase();
  }, [enableFirebase, enableServiceWorker, onInitialized]);

  // ================================================================
  // DEVELOPMENT HELPERS
  // ================================================================
  //
  // Purpose: Provide helpful feedback during development
  // Production: These logs help with debugging configuration issues
  // Environment: Show current configuration status
  // ================================================================

  useEffect(() => {
    if (import.meta.env.DEV && isInitialized) {
      console.group('🔥 Firebase Configuration Status');
      console.log('Environment:', import.meta.env.MODE);
      console.log('Firebase enabled:', enableFirebase);
      console.log('Service worker enabled:', enableServiceWorker);
      console.log('Configuration valid:', isFirebaseConfigured());
      console.log('Initialization error:', initializationError);

      if (enableFirebase && isFirebaseConfigured()) {
        console.log('Project ID:', import.meta.env.VITE_FIREBASE_PROJECT_ID);
        console.log('Has VAPID key:', !!import.meta.env.VITE_FIREBASE_VAPID_KEY);
      }

      console.groupEnd();
    }
  }, [isInitialized, enableFirebase, enableServiceWorker, initializationError]);

  // ================================================================
  // ERROR BOUNDARY CONTEXT
  // ================================================================
  //
  // Purpose: Provide error context to child components
  // Pattern: Allow child components to access initialization status
  // Debugging: Help identify Firebase-related issues
  // ================================================================

  if (!isInitialized) {
    // Show minimal loading indicator while initializing
    return <div className="firebase-initializing">{children}</div>;
  }

  // Render children with Firebase context available
  return (
    <div
      className="firebase-initialized"
      data-firebase-enabled={enableFirebase}
      data-firebase-error={!!initializationError}
    >
      {children}
    </div>
  );
}

/**
 * ================================================================
 * DEVELOPMENT UTILITIES
 * ================================================================
 */

/**
 * Hook to get Firebase initialization status
 * Useful for components that need to know if Firebase is ready
 */
export function useFirebaseInitializationStatus() {
  const [status, setStatus] = useState({
    isConfigured: isFirebaseConfigured(),
    isReady: false,
    error: null as string | null,
  });

  useEffect(() => {
    // Check if Firebase is already initialized
    const checkStatus = () => {
      setStatus({
        isConfigured: isFirebaseConfigured(),
        isReady: !!initializeFirebaseApp(),
        error: null,
      });
    };

    checkStatus();
  }, []);

  return status;
}

/**
 * ================================================================
 * END OF COMPONENT: FirebaseInitializer
 * ================================================================
 *
 * @exports          FirebaseInitializer, useFirebaseInitializationStatus
 * @dependencies     Firebase config, service worker API
 * @usedBy           App.tsx or main.tsx for app-wide Firebase setup
 *
 * @setup
 * 1. Wrap your app with FirebaseInitializer
 * 2. Ensure firebase-messaging-sw.js is in public directory
 * 3. Configure environment variables
 * 4. Test with Firebase console messaging
 *
 * @security
 * - Service worker: Only registers on HTTPS or localhost
 * - Configuration: Validates environment before initialization
 * - Error handling: No sensitive data exposed in error messages
 *
 * ================================================================
 */
